import {ActionMenu, ActionList, Button, LinkButton} from '@primer/react'
import {CopilotIcon, PlusIcon, TriangleDownIcon, FileCheckIcon} from '@primer/octicons-react'
import {useAgentAuthorizationGuard} from '@github-ui/agent-authorization-dialog/guard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useSubAgents} from '@github-ui/copilot-shared-ui/agents/use-sub-agents'
import {useTopLevelAgents} from '@github-ui/copilot-shared-ui/agents/use-top-level-agents'
import {AgentListItems} from './AgentListItems'
import {
  AgentSelectorMenuTrigger,
  AgentSelectorIconTrigger,
  type AgentSelectorTriggerComponent,
} from './AgentSelectorMenuTrigger'

import styles from './AgentSelector.module.css'
import type {DropdownRepository} from './repository'
import {useCallback, useEffect, useMemo} from 'react'
import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import {isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'

type AgentSelectorRepositoryProps =
  | {
      repo: DropdownRepository
      branch: string
    }
  | {
      repo: undefined
      branch: undefined
    }

type AgentSelectorProps = AgentSelectorRepositoryProps & {
  selectedAgent: SelectedAgent
  onSelectAgent: (agent: SelectedAgent) => void
  iconOnly?: boolean
  disabled?: boolean
  buttonProps?: React.ComponentProps<typeof Button>
  triggerComponent?: AgentSelectorTriggerComponent
  triggerClassName?: string
  /**
   * Whether a missing repository is the final answer rather than one still on
   * its way. Sandbox clears the repository on purpose, so without this the
   * absent repository reads as "still resolving" and the trigger spins forever.
   */
  repositoryOptional?: boolean
}

function getNewFileUrl(repo: DropdownRepository, branch: string) {
  const params = new URLSearchParams({
    filename: '.github/agents/my-agent.agent.md',
    value:
      '---\n# Fill in the fields below to create a basic custom agent for your repository.\n# The Copilot CLI can be used for local testing: https://gh.io/customagents/cli\n# To make this agent available, merge this file into the default repository branch.\n# For format details, see: https://gh.io/customagents/config\n\nname:\ndescription:\n---\n\n# My Agent\n\nDescribe what your agent does here.',
  })
  return `/${repo.owner}/${repo.nameOnly}/new/${branch}?${params.toString()}`
}

export function AgentSelector({
  repo,
  branch,
  selectedAgent,
  onSelectAgent,
  iconOnly = true,
  disabled = false,
  buttonProps,
  triggerComponent = AgentSelectorIconTrigger,
  triggerClassName,
  repositoryOptional = false,
}: AgentSelectorProps) {
  const {data: customAgents = [], isLoading: isLoadingCustomAgents} = useSubAgents({repo})
  const {data: topLevelAgents = [], isLoading: isLoadingTopLevel} = useTopLevelAgents(repo?.nameWithOwner)
  const authorizationGuard = useAgentAuthorizationGuard({nwo: repo?.nameWithOwner})
  // UI-flag gate: when off (including ship-dark), skip lock-icon + dialog interception entirely.
  // The server-side gate still fires from sweagentd if a job is submitted for an unauthorized agent.
  const consentUiEnabled = isFeatureEnabled('copilot_swe_agent_authorization_status_ui')

  // A repository that has not arrived yet still counts as loading, but one the
  // caller says will never arrive does not — otherwise Sandbox, which clears the
  // repository deliberately, leaves the trigger spinning with nothing in flight.
  const isAwaitingRepository = !repositoryOptional && (!repo || !branch || repo.isEmpty)
  const isLoading = isAwaitingRepository || isLoadingCustomAgents || isLoadingTopLevel
  // Annotate top-level agents with `requires_authentication` for the lock icon + dialog.
  // Status precedence: API `needs_authorization` > existing `requires_authentication` > mock guard.
  const assignableTopLevelAgents = useMemo(
    () =>
      topLevelAgents
        .filter(a => a.assignable !== false)
        .map(agent => ({
          ...agent,
          requires_authentication: consentUiEnabled
            ? (agent.needs_authorization ??
              agent.requires_authentication ??
              authorizationGuard.requiresAuth(agent.slug, agent.display_name, agent.avatar_url))
            : false,
        })),
    [topLevelAgents, authorizationGuard, consentUiEnabled],
  )
  const agents = useMemo(() => [...topLevelAgents, ...customAgents], [topLevelAgents, customAgents])

  // Intercept selection of an unauthorized agent: open the dialog instead. Same status precedence as above.
  const handleSelectAgent = useCallback(
    (next: SelectedAgent) => {
      if (consentUiEnabled && next.typeName === 'topLevel' && next.login && next.displayName) {
        const matchedAgent = topLevelAgents.find(a => a.slug === next.login)
        const requiresAuth =
          matchedAgent?.needs_authorization ??
          matchedAgent?.requires_authentication ??
          authorizationGuard.requiresAuth(next.login, next.displayName, next.avatarUrl)
        if (requiresAuth) {
          // Resolve integrationId from the agent list; falls back to slug-based mock when missing.
          const integrationId = matchedAgent?.id !== undefined ? Number(matchedAgent.id) : undefined
          authorizationGuard.openDialog({
            slug: next.login,
            displayName: next.displayName,
            avatarUrl: next.avatarUrl,
            integrationId: Number.isFinite(integrationId) ? integrationId : undefined,
          })
          return
        }
      }
      onSelectAgent(next)
    },
    [authorizationGuard, consentUiEnabled, onSelectAgent, topLevelAgents],
  )

  useEffect(() => {
    if (!repo || isLoading || agents.length === 0 || !selectedAgent?.typeName) return

    // When preselected from the assignee picker, the agent only has botGlobalRelayId.
    // Enrich it with the full data (including topLevelAgentId) from the fetched list.
    if (selectedAgent.typeName === 'topLevel' && !selectedAgent.topLevelAgentId) {
      const matchedAgent = agents.find(
        a => 'bot_global_relay_id' in a && a.bot_global_relay_id === selectedAgent.botGlobalRelayId,
      )
      onSelectAgent(
        matchedAgent && 'id' in matchedAgent
          ? {
              typeName: 'topLevel',
              topLevelAgentId: matchedAgent.id,
              displayName: matchedAgent.display_name,
              avatarUrl: matchedAgent.avatar_url,
              login: matchedAgent.slug,
              botGlobalRelayId: matchedAgent.bot_global_relay_id,
              modelVendors: matchedAgent.model_vendors,
            }
          : {},
      )
      return
    }

    // Validate that the previously selected agent still exists in the repo
    const agentExists =
      selectedAgent.typeName === 'topLevel'
        ? agents.some(a => 'id' in a && a.id === selectedAgent.topLevelAgentId)
        : agents.some(a => 'name' in a && a.name === selectedAgent.subAgentSlug)

    if (!agentExists) {
      onSelectAgent({})
    }
  }, [repo, isLoading, agents, selectedAgent, onSelectAgent])

  // Default to "Copilot" if no agent is selected
  const isEmptyAgent = (agent: SelectedAgent) => !agent?.typeName
  const effectiveSelectedAgent = isEmptyAgent(selectedAgent) ? 'Copilot' : selectedAgent

  const agentType =
    typeof effectiveSelectedAgent === 'string'
      ? effectiveSelectedAgent === 'Copilot'
        ? 'topLevel'
        : 'customSubAgent'
      : effectiveSelectedAgent.typeName
  const selectedAgentObj = agents.find(agent => {
    if (typeof effectiveSelectedAgent === 'string') {
      return agent.display_name === effectiveSelectedAgent
    }
    if (agentType === 'topLevel') {
      return (
        ('id' in agent && agent.id === effectiveSelectedAgent.topLevelAgentId) ||
        ('bot_global_relay_id' in agent && agent.bot_global_relay_id === effectiveSelectedAgent.botGlobalRelayId)
      )
    }
    if (agentType === 'customSubAgent') {
      return 'name' in agent && agent.name === effectiveSelectedAgent.subAgentSlug
    }
  })

  const selectedAgentName = (() => {
    if (typeof effectiveSelectedAgent === 'string') {
      return effectiveSelectedAgent
    }
    if (!selectedAgentObj) {
      return undefined
    }
    return selectedAgentObj.display_name || ('name' in selectedAgentObj ? selectedAgentObj.name : undefined)
  })()

  const isCopilotSelected = (() => {
    if (isEmptyAgent(selectedAgent)) {
      return true
    }
    if (selectedAgent.typeName === 'topLevel' && selectedAgentObj && 'slug' in selectedAgentObj) {
      return isCopilotCodingAgent(selectedAgentObj.slug)
    }
    return false
  })()
  const triggerLabel = selectedAgentName || 'None'
  const ariaLabel = selectedAgentName
    ? `${agentType === 'topLevel' ? 'Agent' : 'Custom agent'}: ${selectedAgentName}`
    : `Select an agent`

  const anchor = iconOnly ? (
    <AgentSelectorMenuTrigger
      isLoading={isLoading}
      isCopilotSelected={isCopilotSelected}
      selectedAgentObj={selectedAgentObj}
      agentType={agentType}
      ariaLabel={ariaLabel}
      disabled={disabled}
      className={triggerClassName}
      triggerComponent={triggerComponent}
    />
  ) : (
    <Button
      leadingVisual={selectedAgentName ? FileCheckIcon : CopilotIcon}
      trailingAction={TriangleDownIcon}
      aria-label={ariaLabel}
      className={styles.anchor}
      data-selected={Boolean(selectedAgentObj)}
      disabled={disabled || isLoading}
      {...buttonProps}
    >
      {triggerLabel}
    </Button>
  )

  return (
    <>
      <ActionMenu>
        <ActionMenu.Anchor>{anchor}</ActionMenu.Anchor>
        <ActionMenu.Overlay width="auto" overflow="auto">
          {!isLoading &&
            (agents.length === 0 ? (
              <EmptyState newFileUrl={repo && branch ? getNewFileUrl(repo, branch) : undefined} />
            ) : (
              <ActionList>
                <AgentListItems
                  topLevelAgents={assignableTopLevelAgents}
                  customAgents={customAgents}
                  selectedAgent={selectedAgent}
                  onSelectAgent={handleSelectAgent}
                />
                {repo && branch ? (
                  <>
                    <ActionList.Divider />
                    <ActionList.Group selectionVariant={false}>
                      <ActionList.LinkItem href={getNewFileUrl(repo, branch)} target="_blank">
                        <ActionList.LeadingVisual>
                          <PlusIcon />
                        </ActionList.LeadingVisual>
                        Create a custom agent
                      </ActionList.LinkItem>
                    </ActionList.Group>
                  </>
                ) : null}
              </ActionList>
            ))}
        </ActionMenu.Overlay>
      </ActionMenu>
      {authorizationGuard.dialog}
    </>
  )
}

const EmptyState = ({newFileUrl}: {newFileUrl?: string}) => (
  <div className={styles.emptyStateContainer}>
    <h3 className={styles.emptyStateTitle}>
      {newFileUrl ? 'This repository has no custom agents' : 'No custom agents available'}
    </h3>
    <p className={styles.emptyStateDescription}>
      {newFileUrl
        ? 'Custom agents are reusable instructions and tools in your repository.'
        : 'Custom agents come from a repository. Select one to use its agents.'}
    </p>
    {newFileUrl ? <LinkButton href={newFileUrl}>Create a custom agent</LinkButton> : null}
  </div>
)
